<script setup async>
import {ProtocolApproval} from '@/common/api.service'
import {useRoute, useRouter} from 'vue-router'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import shared from '@/common/shared'
import {useDropzone} from 'vue3-dropzone'
import {useToast} from 'vue-toastification'
import moment from 'moment/moment'
import ECP from '@/components/partial/ECP.vue'
import ValidateInput from '@/components/validate/input.vue'
import Pagination from '@/components/partial/Pagination.vue'
import {useStore} from 'vuex'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const store = useStore()
const list = ref({
  columns: [], directories: {}, meta: {
    total: 0, per_page: 0, total_filtered: 0, PLANS: [], USER_CONSONANTS: []
  }
})
const currentPage = ref(1)
const currentLimit = ref(10)
const plan = ref({
  USER_SIGNS: [0, 0, 0, 0, 0, 0],
  UF_NAME: '',
  UF_NUMBER: '',
  UF_COST: NaN
})
const docFiles = ref([])
const request = ref({
  is_initial_load: 1, page: currentPage.value, limit: currentLimit.value, filters: {}
})
const disableSave = ref(true)
const showStatus = ref(false)
const showSign = ref(false)
const dragStarted = ref(false)
const validationErrors = ref({})
const dbl = ref(0)
const disableEdit = ref(false)
const checkCantSend = ref(true)
const revoke = ref({comment: ''})
const disableRevoke = ref(false)

if (route.query._r) {
  request.value = JSON.parse(route.query._r)
  currentPage.value = request.value.page
  currentLimit.value = request.value.limit
}
const pages = computed(() => {
  if (
      !list.value.meta.total_filtered ||
      list.value.meta.total_filtered <= list.value.meta.per_page
  ) {
    return []
  }
  return [
    ...Array(Math.ceil(list.value.meta.total_filtered / list.value.meta.per_page)).keys()
  ].map(e => e + 1)
})

const allList = computed(() => {
  let r = []

  if (list.value.meta.ALL !== undefined) {
    list.value.meta.ALL.forEach(function (f) {
      if (parseInt(f.UF_NUMBER) > 0) {
        r.push({value: f.UF_NUMBER, label: `${f.UF_NUMBER}`})
      }
    })
  }

  r.sort(function (a, b) {
    if (a.value > b.value) {
      return 1;
    }
    if (a.value < b.value) {
      return -1;
    }
    return 0;
  });

  return r
})

const yearList = computed(() => {
  let r = []

  if (list.value.meta.PLAN_LIST !== undefined) {
    list.value.meta.PLAN_LIST.forEach(function (f) {
      r.push({value: f.ID, label: `${f.UF_YEAR}`})
    })
  }

  return r
})

const approvalPdList = computed(() => {
  let r = []
  let selectApprovalProcurmentList = []

  if (request.value.filters.UF_PLAN_ID && request.value.filters.UF_PLAN_ID.length > 0) {
    list.value.meta.APPROVAL_PROCUREMENT_DOCUMENTATION_LIST.forEach(function (f) {
      if (
          request.value.filters.UF_PLAN_ID.includes(f.PURCHASE_REQUEST.PURCHASE_PLAN.UF_PLAN_ID) &&
          (f.PURCHASE_REQUEST.UF_NOTICE_NUMBER !== '' && f.PURCHASE_REQUEST.UF_NOTICE_NUMBER !== null)
      ) {
        selectApprovalProcurmentList.push({value: f.ID, label: `${f.UF_SUBJECT}`})
      }
    })
    r = selectApprovalProcurmentList

  }else {
    list.value.meta.APPROVAL_PROCUREMENT_DOCUMENTATION_LIST.forEach(function (f) {
      r.push({value: f.ID, label: `${f.UF_SUBJECT}`})
    })
  }

  return r
})

const noticeNumberList = computed(() => {
  let r = []

  let selectNoticeNumber = []
  if (request.value.filters.UF_PURCHASE_PLAN_ID && request.value.filters.UF_PURCHASE_PLAN_ID.length > 0) {
    list.value.meta.PURCHASE_PLAN_LIST.forEach(function (f) {
      if (
          request.value.filters.UF_PURCHASE_PLAN_ID.includes(f.ID) &&
          (f.PURCHASE_REQUEST.UF_NOTICE_NUMBER !== '' && f.PURCHASE_REQUEST.UF_NOTICE_NUMBER !== null)
      ) {
        selectNoticeNumber.push(f.PURCHASE_REQUEST.UF_NOTICE_NUMBER)
      }
    })
    r = selectNoticeNumber
  }else{
    if (list.value.meta.NOTICE_NUMBERS !== undefined) {
      list.value.meta.NOTICE_NUMBERS.forEach(function (f) {
        if (f !== null && f !== undefined && f !== '') {
          r.push({value: f, label: `${f}`})
        }
      })
    }
  }

  return r
})

const statusList = computed(() => {
  let r = []
  if (list.value.meta.STATUSES !== undefined) {
    list.value.meta.STATUSES.forEach(function (f) {
      r.push({value: f.ID, label: `${f.UF_NAME}`})
    })
  }

  return r
})

watch(currentLimit, async () => {
  request.value.limit = currentLimit.value
  await fetchList()
})

watch(currentPage, async () => {
  request.value.page = currentPage.value
  await fetchList()
})

const validateForm = async () => {
  if (showStatus.value) {
    return
  }
  checkCantSend.value = false
  if (!plan.value.UF_APPROVAL_PD_ID || !plan.value.UF_SUBJECT || !plan.value.UF_COST || !plan.value.USER_SIGNS[0]) {
    checkCantSend.value = true
    return
  }
  if (!plan.value.ID && !docFiles.value.length) {
    checkCantSend.value = true
    return
  }
  if (plan.value.ID && !docFiles.value.length && !plan.value.DOCUMENTS.length) {
    checkCantSend.value = true
    return
  }
}

watch(plan, validateForm, {deep: true})
watch(docFiles, validateForm, {deep: true})

async function fetchList() {
  await ProtocolApproval.list(request.value)
      .then(({data}) => {
        list.value = data
        if (data.order) {
          request.value.order = data.order
        }
        request.value.filters = data.filters
        request.value.order_empty = 1
        delete request.value.is_initial_load
        router.push({
          query: {
            _r: JSON.stringify(request.value)
          }
        })
      }).catch(() => {
      })
}

await fetchList()

async function resetFilter() {
  request.value.filters = {}
  await fetchList()
}

const procurementDocList = computed(() => {
  let r = []
  if (list.value.meta.APPROVAL_PROCUREMENT_DOCUMENTATION_LIST_EXTENDED) {
    list.value.meta.APPROVAL_PROCUREMENT_DOCUMENTATION_LIST_EXTENDED.forEach(function (f) {
      r.push({value: f.ID, label: `${f.UF_SUBJECT}`})
    })
  }

  return r
})

const columnList = computed(() => {
  return list.value.columns.filter((el) => el.visible)
})

function setValidationErrors(dt) {
  if (!dt) {
    validationErrors.value = {
      UF_SUBJECT: [],
      UF_PM_ID: [],
      UF_COST: [],
      UF_APPROVAL_PD_ID: [],
    }
  } else {
    validationErrors.value = dt
  }
}

async function upPurchase() {
  if (!plan.value.ID) {
    let purchase = list.value.meta.APPROVAL_PROCUREMENT_DOCUMENTATION_LIST_EXTENDED.find(v => v.ID === plan.value.UF_APPROVAL_PD_ID)
    if (purchase) {
      plan.value.UF_SUBJECT = purchase.UF_SUBJECT
      plan.value.UF_PM_ID = purchase.UF_PM_ID
      plan.value.UF_PM = (purchase.PM) ? purchase.PM.UF_NAME : ''
      plan.value.UF_NOTICE_NUMBER = purchase.PURCHASE_REQUEST.UF_NOTICE_NUMBER
      plan.value.UF_COST = purchase.UF_COST
    }
  }
}

async function routeNewPlan() {
  await router.push({name: 'ListProtocolAdd', query: {_r: route.query._r}})
}

async function showNewPlan() {
  docFiles.value = []
  plan.value = {
    UF_NAME_APPROVAL_PP_ID: 0,
    USER_SIGNS: [0, 0, 0, 0, 0, 0],
    UF_TERM_SERVICES_WORKS: '',
    UF_COST: NaN,
    UF_SUBJECT: '',
    UF_NAME: '',
    UF_NUMBER: '',
    UF_APPROVAL_PD_ID: '',
    UF_DATE_CREATE: moment().format('DD.MM.YYYY'),
    UF_CREATED_BY: store.getters.currentUser.FULL_NAME,
  }
  list.value.meta.USER_CONSONANTS.filter(v => v.PROFILE.UF_IS_DEFAULT_AGREEMENT === true).forEach((v, index) => {
    if (index > 2) {//по умолчанию выводим только 2 согласующих
      return
    }
    plan.value.USER_SIGNS[index] = v.ID
  })

  disableEdit.value = false;

  setValidationErrors()
  shared.showModal('#modal-add-plan-zakupku-ypz')
  dbl.value++
}

async function saveNewPlan() {
  let formData = new FormData()

  formData.append('UF_APPROVAL_PD_ID', plan.value.UF_APPROVAL_PD_ID)
  formData.append('UF_SUBJECT', plan.value.UF_SUBJECT)
  formData.append('UF_PM_ID', plan.value.UF_PM_ID)
  formData.append('UF_COST', plan.value.UF_COST)
  formData.append('UF_TERM_SERVICES_WORKS', plan.value.UF_TERM_SERVICES_WORKS)
  formData.append('UF_NAME', plan.value.UF_NAME)
  formData.append('UF_NUMBER', plan.value.UF_NUMBER)

  plan.value.USER_SIGNS.forEach((v) => {
    if (v > 0) {
      formData.append('USER_SIGNS[]', v)
    }
  })
  docFiles.value.forEach((v, k) => {
    formData.append(`DOCUMENTS[${k}]`, v)
  })
  await ProtocolApproval.create(formData)
      .then(async () => {
        await fetchList()
        shared.closeModal('#modal-add-plan-zakupku-ypz')
        await router.push({name: 'ListProtocol', query: {_r: route.query._r}})
        dbl.value++
      }).catch(({response}) => {
        validationErrors.value = response.data.errors
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

async function routeEditPlan(item) {
  await router.push({name: 'ListProtocolEdit', params: {id: item}, query: {_r: route.query._r}})
}

async function showEditPlan(item) {
  shared.closeModal('#plan-revoke')
  docFiles.value = []
  await ProtocolApproval.item(item)
      .then(async ({data}) => {
        plan.value = data
        //plan.value.UF_PURCHASE_PLAN_ID = data.PURCHASE_PLAN.ID
        data.UF_DATE_CREATE = moment(data.UF_DATE_CREATE).format('DD.MM.YYYY')

        let us = JSON.stringify(data.USER_SIGNS)
        plan.value.USER_SIGNS_STATUS = JSON.parse(us)
        let allAgreed = true
        data.USER_SIGNS.forEach((v, index) => {
          if (v.STATUS && v.STATUS.UF_CODE !== 'agreed') {
            allAgreed = false
          }
          plan.value.USER_SIGNS[index] = v.UF_USER_ID
        })
        data.ALL_AGREED = allAgreed

        if (data.UF_TERM_SERVICES_WORKS == 'null' || data.UF_TERM_SERVICES_WORKS == 'undefined') {
          plan.value.UF_TERM_SERVICES_WORKS = ''
        }

        plan.value.UF_CREATED_BY = data.CREATED_BY.FULL_NAME

        if (data.USER_SIGNS.length <= 6) {
          for (let i = data.USER_SIGNS.length; i <= 5; i++) {
            plan.value.USER_SIGNS[i] = 0
          }
        }

        if (plan.value.STATUS.UF_CODE === 'agreed' || !plan.value.IS_EDIT) {
          disableEdit.value = true
        } else {
          disableEdit.value = false
        }

        //plan.value.UF_PM = data.PM.UF_NAME
        plan.value.UF_PM = data.PM.UF_NAME
        plan.value.UF_NOTICE_NUMBER =  data.PURCHASE_REQUEST.UF_NOTICE_NUMBER

        shared.showModal('#modal-add-plan-zakupku-ypz')
        dbl.value++
        planUnwatch = watch(
            plan,
            async () => {
              disableSave.value = false
            },
            {deep: true}
        )
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

async function savePlan() {
  let formData = new FormData()

  formData.append('UF_APPROVAL_PD_ID', plan.value.UF_APPROVAL_PD_ID)
  formData.append('UF_SUBJECT', plan.value.UF_SUBJECT)
  formData.append('UF_PM_ID', plan.value.UF_PM_ID)
  formData.append('UF_COST', plan.value.UF_COST)
  formData.append('UF_TERM_SERVICES_WORKS', plan.value.UF_TERM_SERVICES_WORKS)
  formData.append('UF_NAME', plan.value.UF_NAME)
  formData.append('UF_NUMBER', plan.value.UF_NUMBER)

  plan.value.USER_SIGNS.forEach((v) => {
    if (v > 0) {
      formData.append('USER_SIGNS[]', v)
    }
  })

  let userDocumentIndex = 0
  await plan.value.DOCUMENTS.forEach((v) => {
    formData.append(`DOCUMENTS[${userDocumentIndex++}]`, v.ID)
  })
  docFiles.value.forEach((v) => {
    formData.append(`DOCUMENTS[${userDocumentIndex++}]`, v)
  })
  await ProtocolApproval.update(plan.value.ID, formData)
      .then(async () => {
        await fetchList()
        await closeEditPlan()
        dbl.value++
      }).catch(({response}) => {
        validationErrors.value = response.data.errors
        toast.error(response.data.message, {
          timeout: 5000
        })
        dbl.value++
      })
}

let planUnwatch = function () {
}

async function closeEditPlan() {
  planUnwatch()
  await router.push({name: 'ListProtocol', query: {_r: route.query._r}})
  shared.closeModal('#modal-add-plan-zakupku-ypz')
}

const {getRootProps, getInputProps} = useDropzone({onDrop})

function onDrop(acceptFiles) {
  validationErrors.value.DOCUMENTS = []
  acceptFiles.forEach(v => {
    docFiles.value.push(v)
  })
  disableSave.value = false
  dragStarted.value = false
}

const dragEnter = () => {
  dragStarted.value = true
}
const dragEnd = () => {
  console.log('dragEnd')
}

async function onAddDocFile() {

}

async function planAllDocs(id) {
  await ProtocolApproval.allDocs(id)
      .then(async ({data}) => {
        document.location.href = data.url
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function signList(id) {
  await ProtocolApproval.signList(id)
      .then(async ({data}) => {
        document.location.href = data.url
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showSignStatus(item) {
  await ProtocolApproval.item(item)
      .then(async ({data}) => {
        data.UF_DATE_CREATE = moment(data.UF_DATE_CREATE).format('DD.MM.YYYY')
        plan.value = data
        showStatus.value = true
        shared.showModal('#modal-plan-zakupku-ypz-signatories')
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showPlanRevoke() {
  shared.closeModal('#modal-add-plan-zakupku-ypz')
  shared.showModal('#plan-revoke')
  revoke.value.comment = ''
}

async function planRevoke() {
  disableRevoke.value = true
  await ProtocolApproval.revoke(plan.value.ID, {
    reason: revoke.value.comment
  })
      .then(async () => {
        await fetchList()
        shared.closeModal('#plan-revoke')
        disableRevoke.value = false
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

async function showSignModal() {
  shared.closeModal('#modal-add-plan-zakupku-ypz')
  showSign.value = true
}

async function closeModalSign() {
  showSign.value = false
  shared.showModal('#modal-add-plan-zakupku-ypz')
  await showEditPlan(plan.value.ID)
}

async function sendEds(val) {
  await ProtocolApproval.sign(plan.value.ID, {
    UF_EDS: val,
  })
      .then(async () => {
        await fetchList(route.params.id)
        await closeModalSign()
      }).catch(({response}) => {
        toast.error(response.data.message, {
          timeout: 5000
        })
      })
}

function selectPlaceholderColor() {
  let selects = document.querySelectorAll('select.c-select')
  selects.forEach(select => {

    // Добавляем слушатель события изменения значения
    select.addEventListener('change', function () {

      if (this.value == 0) {
        this.style.color = ''
      } else {
        this.style.color = '#000'
      }
    })
  })
}

async function copyURL() {
  try {
    await navigator.clipboard.writeText(window.location.href)
    toast.success('Ссылка на карточку скопирована', {
      timeout: 2000
    })
  } catch ($e) {
    toast.error('При копировании ссылки на карточку произошла ошибка', {
      timeout: 2000
    })
  }
}

onMounted(async () => {
  await router.isReady()
  selectPlaceholderColor()
  if (route.name === 'ListProtocolAdd') {
    await showNewPlan()
  } else if (route.name === 'ListProtocolEdit') {
    await showEditPlan(route.params.id)
  }
})
onUnmounted(() => {
  selectPlaceholderColor()
})
</script>

<template>
  <div class="main-wraper" id="js-main-wraper">
    <MainHeader></MainHeader>
    <!-- header -->
    <section class="content-wrapper" id="js-content-wrapper">
      <div class="container-fluid">
        <section class="page-title-container stl-mb-30 stl-pt-20">
          <div class="page-title-container__col">
            <h1 class="title-page">Согласование протоколов</h1>
          </div>
          <div class="page-title-container__col page-title-container__controll">
            <a @click="routeNewPlan" v-if="list.meta.IS_EDIT"
               data-m class="btn btn-blue">Добавить согласование</a>
          </div>
        </section>
        <!-- page-title-container -->

        <section class="box-stl c-filter--container">
          <div class="text-center" id="js-filter-show"><span class="c-filter--mob-link stl-tt-up"
                                                             onclick="filterMobileShow();">Развернуть фильтр</span>
          </div>
          <div class="c-filter--grid df-jsb df-none-wrap df-al" id="js-filter-grid">
            <div class="c-filter--filter-area">
              <div class="c-filter__item" v-show="false">
                <Multiselect
                    class="c-input c-select c-filter__input c-multiselect-filter"
                    v-model="request.filters.UF_NUMBER"
                    mode="tags"
                    :searchable="true"
                    :options="allList"
                    :show-no-results="false"
                    placeholder="№"
                />
              </div>
              <div class="c-filter__item">
                <Multiselect
                    class="c-input c-select c-filter__input c-multiselect-filter"
                    v-model="request.filters.UF_PLAN_ID"
                    mode="tags"
                    :searchable="true"
                    :options="yearList"
                    :show-no-results="false"
                    placeholder="Год"
                />
              </div>
              <div class="c-filter__item">
                <Multiselect
                    class="c-input c-select c-filter__input c-multiselect-filter"
                    v-model="request.filters.UF_APPROVAL_PD_ID"
                    mode="tags"
                    :searchable="true"
                    :options="approvalPdList"
                    :show-no-results="false"
                    placeholder="Закупка"
                />
              </div>
              <div class="c-filter__item">
                <Multiselect
                    class="c-input c-select c-filter__input c-multiselect-filter"
                    v-model="request.filters.UF_NOTICE_NUMBER"
                    mode="tags"
                    :searchable="true"
                    :options="noticeNumberList"
                    :show-no-results="false"
                    placeholder="№ извещения"
                />
              </div>
              <div class="c-filter__item">
                <Multiselect
                    class="c-input c-select c-filter__input c-multiselect-filter"
                    v-model="request.filters.STATUS"
                    mode="tags"
                    :options="statusList"
                    :show-no-results="false"
                    placeholder="Статус"
                />
              </div>
              <div class="c-filter__controll">
                <div class="btn btn-reset" @click="resetFilter">СБРОС</div>
                <div class="btn btn-blue" @click="fetchList">Применить</div>
              </div>
            </div>
          </div>
          <!-- c-filter--grid -->
        </section>

        <section class="page-container">
          <div class="box-stl c-table--container">
            <div class="holder-responsive">
              <table class="c-table c-table-interface-bs-b c-table-interface-mb-0">
                <thead class="thead-stl-fw-bold">
                <tr>
                  <th :key="column.name" v-for="column in columnList">
                    <div class="sorting-holder" v-if="column.type==='column'">
                      <span>{{ column.title }}</span>
                    </div>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr :key="item.ID" v-for="item in list.data">
                  <td :key="column.name" v-for="column in columnList">
                                        <span
                                            v-if="
                                            column.name!=='STATUS' &&
                                            column.name!=='UF_NUMBER' &&
                                             column.name!=='UF_SUBJECT' &&
                                            (column.display_as === 'text' || column.display_as === 'text_path')"
                                            :style="{
                      'white-space': column.modifiers.find(val => val === 'white-space-pre')?'pre':'',
                      'white-space': column.name==='AWAITING_APPROVAL' ||  column.name==='AGREED'?'pre-line':'',
                      //'word-break': column.modifiers.find(val => val === 'break-all')?'break-all':'',
                      'color': column.display_as_config.text_color_path?item.STATUS.color:''
                    }"
                                        >{{ shared.showField(column, item) }}
                  </span>

                    <a class="stl-clr-blue stl-clr-td-n" href="#" v-if="column.name==='PLAN'"
                       @click="routeEditPlan(item.ID)">
                      {{ shared.showField(column, item).UF_YEAR }}
                    </a>

                    <router-link :to="{name: 'CardPurchase',params: { id: item.UF_APPROVAL_PD_ID }}"
                                 v-if="column.name==='UF_NUMBER'"> {{ item.UF_NUMBER }}
                    </router-link>
                    <a class="stl-clr-blue stl-clr-td-n" href="javascript:void(0);" v-if="column.name==='UF_SUBJECT'"
                       @click="routeEditPlan(item.ID)">
                      {{ item.UF_SUBJECT }}
                    </a>
                    <a
                        :class="(column.display_as_config.text_color_path && item.STATUS.color)?'stl-clr-'+item.STATUS.color:'stl-clr-orange'"
                        class=" stl-clr-td-n"
                        href="#"
                        :style="{
                      'white-space': column.modifiers.find(val => val === 'white-space-pre')?'pre':'',
                      'word-break': column.modifiers.find(val => val === 'break-all')?'break-all':'',
                      'color_': column.display_as_config.text_color_path?item.STATUS.color:''
                    }"
                        v-if="column.name==='STATUS'" @click="showSignStatus(item.ID)">
                      {{ shared.showField(column, item) }}
                    </a>
                  </td>
                </tr>
                </tbody>
              </table>
              <Pagination
                  :pages="pages"
                  v-model="currentPage"
                  :total="list.meta.total_filtered"
                  :perPage="currentLimit"
                  @update:perPage="currentLimit = $event"
              ></Pagination>
            </div>
          </div>
        </section>
        <!-- page-container -->
      </div>
    </section>
    <MainFooter>
      <div class="footer-right__controll">

      </div>
    </MainFooter>
    <!-- footer -->
  </div>
  <!-- main-wraper -->
  <!-- Утверждение Плана закупки - Добавить План закупки  -->
  <div class="modal modal--size-full" id="modal-add-plan-zakupku-ypz" tabindex="-1" @dragenter="dragEnter"
       @dragleave="dragEnd">
    <div class="modal-dialog modal-xl modal-dialog-centered" v-if="!showStatus">
      <div class="modal-content">
        <div class="modal-header" v-if="!plan.ID">
          <h5 class="modal-title">Добавить согласование протокола</h5>
        </div>
        <div class="modal-header" v-else>
          <h5 class="modal-title">Согласование протокола</h5>
          <a
              href="javascript:void(0);"
              @click="copyURL"
              style="text-decoration: underline; float: right; padding-right: 10px;"
          >Ссылка</a>
        </div>
        <div class="modal-body">
          <form action="#">
            <div class="modal-grid-row --jsb">
              <div class="modal-grid-row-stl stl-mb-20">
                <div class="df-fc">
                  <ValidateInput
                      :name="'Закупка'"
                      required
                      :errors="validationErrors.UF_APPROVAL_PD_ID"
                      v-model="plan.UF_APPROVAL_PD_ID"
                      label-class="form-label"
                  >
                    <template v-slot:field="{validationFailed}">
                      <Multiselect
                          class="modal-multiselect-filter c-input c-select c-filter__input c-multiselect-filter stl-color-black"
                          v-model="plan.UF_APPROVAL_PD_ID"
                          mode="single"
                          :disabled="plan.ID"
                          :class="{'unvalidate': validationFailed}"
                          @select="upPurchase"
                          :show-no-results="false"
                          :searchable="true"
                          :options="procurementDocList"
                          :errors="validationErrors.UF_APPROVAL_PD_ID"
                          placeholder="Закупка"
                          :no-options-text="'Введите закупку'"
                      />
                    </template>
                  </ValidateInput>
                </div>
                <div class="df-fc">
                  <ValidateInput :placeholder="'№ извещения'"
                                 :name="'№ извещения'"
                                 :show-errors="false"
                                 :disabled="true"
                                 label-class="form-label"
                                 v-model="plan.UF_NOTICE_NUMBER"></ValidateInput>
                </div>
                <div class="df-fc">
                  <ValidateInput :placeholder="'Дата создания'"
                                 :name="'Дата создания'"
                                 :show-errors="false"
                                 required
                                 disabled
                                 label-class="form-label"
                                 v-model="plan.UF_DATE_CREATE"></ValidateInput>
                </div>
                <div class="df-fc">
                  <ValidateInput
                      :name="'Инициатор'"
                      :show-errors="false"
                      required
                      disabled
                      label-class="form-label"
                      v-model="plan.UF_CREATED_BY"></ValidateInput>
                </div>
              </div>
              <div class="stl-mb-20">
                <ValidateInput :placeholder="'Предмет закупки'"
                               :name="'Предмет закупки'"
                               :show-errors="false"
                               :required="true"
                               :disabled="plan.ID && !plan.IS_EDIT"
                               :errors="validationErrors.UF_SUBJECT"
                               label-class="form-label"
                               v-model="plan.UF_SUBJECT"></ValidateInput>
              </div>
              <div class="stl-mb-20">
                <ValidateInput :placeholder="'Способ закупки'"
                               :name="'Способ закупки'"
                               :show-errors="false"
                               required
                               disabled
                               :errors="validationErrors.UF_PM_ID"
                               label-class="form-label"
                               v-model="plan.UF_PM"></ValidateInput>
              </div>
              <div class="modal-grid-row-stl stl-mb-20">
                <div class="df-fc">
                  <ValidateInput :placeholder="'Ваш текст'"
                                 :name="'Срок услуги/поставки работ'"
                                 :show-errors="false"
                                 :disabled="plan.ID && !plan.IS_EDIT"
                                 label-class="form-label"
                                 v-model="plan.UF_TERM_SERVICES_WORKS"></ValidateInput>
                </div>
                <div class="df-fc">
                  <ValidateInput :placeholder="'Сумма'"
                                 :name="'НМЦ'"
                                 :show-errors="false"
                                 number
                                 required
                                 :disabled="plan.ID && !plan.IS_EDIT"
                                 :spaces="true"
                                 :errors="validationErrors.UF_COST"
                                 :float-format="0"
                                 label-class="form-label"
                                 v-model="plan.UF_COST"></ValidateInput>
                </div>
              </div>
              <div class="modal-grid-row-stl stl-mb-20">
                <div class="df-fc">
                  <ValidateInput :placeholder="'Введите наименование протокола'"
                                 :name="'Наименование протокола'"
                                 :show-errors="false"
                                 required
                                 :disabled="plan.ID && !plan.IS_EDIT"
                                 label-class="form-label"
                                 v-model="plan.UF_NAME"></ValidateInput>
                </div>
                <div class="df-fc">
                  <ValidateInput :placeholder="'№ протокола'"
                                 :name="'Введите № протокола'"
                                 required
                                 :show-errors="false"
                                 :disabled="plan.ID && !plan.IS_EDIT"
                                 label-class="form-label"
                                 v-model="plan.UF_NUMBER"></ValidateInput>
                </div>
              </div>
            </div>
            <div class="h4-title stl-mb-10">Согласующие</div>
            <div class="modal-grid-row modal-grid-row-stl --fw">
              <ValidateInput
                  v-for="i in [0,1,2,3,4,5]" :key="i"
                  :name="'Согласующий №'+(i+1)"
                  :required="i===0"
                  :errors="validationErrors.USER_SIGNS"
                  v-model="plan.USER_SIGNS[i]">
                <template v-slot:field="{validationFailed}">
                  <select v-model="plan.USER_SIGNS[i]"
                          id=""
                          :class="{'unvalidate': validationFailed}"
                          class="c-input c-select--black_ c-select"
                          :disabled="disableEdit || (plan.STATUS && plan.STATUS.UF_CODE === 'revoked') ||
                          (plan.USER_SIGNS_STATUS && plan.USER_SIGNS_STATUS[i] &&
                          plan.USER_SIGNS_STATUS[i].STATUS && plan.USER_SIGNS_STATUS[i].STATUS.UF_CODE !== 'on_approval')"
                          :style="(plan.USER_SIGNS[i] > 0) ? 'color:#000' : ''">
                    <option :value="0">Выберите из списка</option>
                    <option :value="i.ID" v-for="i in list.meta.USER_CONSONANTS" :key="i">{{ i.FULL_NAME }}</option>
                  </select>
                </template>
              </ValidateInput>
            </div>
            <div class="modal-grid-row" :class="{
                      unvalidate: validationErrors.DOCUMENTS && validationErrors.DOCUMENTS.length > 0
                    }">
              <div class="h4-title stl-mb-10">Документы <span class="c-c-danger">*</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                     class="stl-cursor-pointer"
                     @click="planAllDocs(plan.ID)"
                     v-if="plan.ID && plan.DOCUMENTS.length >0">
                  <mask id="mask0_550_4065" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20"
                        height="20">
                    <rect width="20" height="20" fill="#D9D9D9"/>
                  </mask>
                  <g mask="url(#mask0_550_4065)">
                    <path
                        d="M14.3751 14.8381L13.2645 13.7275C13.149 13.6122 13.0054 13.5546 12.8334 13.5546C12.6615 13.5546 12.5178 13.6122 12.4024 13.7275C12.287 13.8429 12.2292 13.9884 12.2292 14.164C12.2292 14.3395 12.287 14.4841 12.4024 14.5977L14.4728 16.6763C14.6235 16.8269 14.7992 16.9023 15.0001 16.9023C15.2009 16.9023 15.3767 16.8269 15.5274 16.6763L17.6059 14.5977C17.7212 14.4841 17.7788 14.3395 17.7788 14.164C17.7788 13.9884 17.7212 13.8429 17.6059 13.7275C17.4905 13.6122 17.345 13.5546 17.1695 13.5546C16.9939 13.5546 16.8493 13.6122 16.7357 13.7275L15.6251 14.8381V11.6875C15.6251 11.5104 15.5652 11.362 15.4453 11.2423C15.3254 11.1224 15.177 11.0625 14.9999 11.0625C14.8227 11.0625 14.6742 11.1224 14.5547 11.2423C14.4349 11.362 14.3751 11.5104 14.3751 11.6875V14.8381ZM12.5001 18.5417H17.5001C17.6772 18.5417 17.8256 18.6016 17.9453 18.7215C18.0652 18.8413 18.1251 18.9898 18.1251 19.1669C18.1251 19.3441 18.0652 19.4925 17.9453 19.6121C17.8256 19.7318 17.6772 19.7917 17.5001 19.7917H12.5001C12.323 19.7917 12.1746 19.7317 12.0549 19.6119C11.935 19.492 11.8751 19.3435 11.8751 19.1665C11.8751 18.9892 11.935 18.8408 12.0549 18.7212C12.1746 18.6015 12.323 18.5417 12.5001 18.5417ZM5.04821 16.4583C4.63793 16.4583 4.28432 16.3098 3.98737 16.0127C3.69029 15.7158 3.54175 15.3622 3.54175 14.9519V3.38146C3.54175 2.97118 3.69029 2.61757 3.98737 2.32063C4.28432 2.02354 4.63793 1.875 5.04821 1.875H10.2101C10.4133 1.875 10.607 1.91403 10.7911 1.99208C10.9753 2.07 11.1358 2.17736 11.2726 2.31417L15.1859 6.2275C15.3227 6.36431 15.4301 6.52479 15.508 6.70896C15.5861 6.89313 15.6251 7.08681 15.6251 7.29V8.61063C15.6251 8.78771 15.5652 8.93611 15.4453 9.05583C15.3254 9.17569 15.177 9.23563 14.9999 9.23563C14.8227 9.23563 14.6742 9.17569 14.5547 9.05583C14.4349 8.93611 14.3751 8.78771 14.3751 8.61063V7.29167H11.3382C11.0245 7.29167 10.7577 7.18181 10.538 6.96208C10.3183 6.74236 10.2084 6.47563 10.2084 6.16188V3.125H5.04821C4.98404 3.125 4.92529 3.15174 4.87196 3.20521C4.81848 3.25854 4.79175 3.31729 4.79175 3.38146V14.9519C4.79175 15.016 4.81848 15.0748 4.87196 15.1281C4.92529 15.1816 4.98404 15.2083 5.04821 15.2083H9.42321C9.60029 15.2083 9.74869 15.2683 9.86841 15.3881C9.98828 15.508 10.0482 15.6565 10.0482 15.8335C10.0482 16.0108 9.98828 16.1592 9.86841 16.2787C9.74869 16.3985 9.60029 16.4583 9.42321 16.4583H5.04821Z"
                        fill="#0D74BB"/>
                  </g>
                </svg>
              </div>
              <ol class="documents-list">
                <li v-for="(docFile, index) in plan.DOCUMENTS" :key="docFile">
                  <div class="df-ac">
                    <a class="stl-mr-10" :href="docFile.FILE.URL_DOWNLOAD">{{ docFile.FILE.FILE_NAME }}</a>
                    <span class="stl-cursor-pointer df-ac holder-delete-file"
                          v-if="plan.STATUS.UF_CODE !== 'revoked' && !disableEdit"
                          @click="plan.DOCUMENTS.splice(index, 1);disableSave=false"></span>
                  </div>
                </li>
                <li v-for="(docFile, index) in docFiles" :key="docFile">
                  <div class="df-ac">
                    <span class="stl-mr-10">{{ docFile.name }}</span>
                    <span class="stl-cursor-pointer df-ac holder-delete-file" @click="docFiles.splice(index, 1)"></span>
                  </div>
                </li>
              </ol>
              <div
                  v-if="!disableEdit"
                  class="file-upload-multiple" v-bind="getRootProps()"
                  :class="{
                'drag-started':dragStarted,
                'unvalidate': validationErrors.DOCUMENTS && validationErrors.DOCUMENTS.length > 0
              }">
                <input v-bind="getInputProps()" :multiple="true" @change="onAddDocFile">
                <span class="file-upload-multiple-label">Выберите файлы или перетащите сюда</span>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-red" v-if="plan.ID && plan.IS_REVOKE && store.getters.currentUser.ACTIVE_GROUP.CODE !=='consonants'" @click="showPlanRevoke">отозвать
          </button>
          <button type="button" class="btn btn-green"
                  v-if="plan.ID && plan.IS_SIGN && plan.STATUS.UF_CODE !== 'revoked'" @click="showSignModal">подписать
          </button>
          <button type="button" class="btn btn-blue" v-if="plan.ID && plan.STATUS.UF_CODE !== 'revoked' && !disableEdit"
                  @click="savePlan" :key="dbl"
                  :class="{'--disabled': !plan.ID && disableSave}"
                  :disabled="!plan.ID && disableSave">
            сохранить изменения
          </button>
          <button type="button" class="btn"
                  v-if="!plan.ID"
                  :class="{
                    'btn-transparent':checkCantSend,
                    'btn-blue': !checkCantSend
                  }"
                  @click.once="saveNewPlan" :disabled="checkCantSend" :key="dbl">отправить на согласование
          </button>
          <button type="button" class="btn "
                  v-if="plan.ID && store.getters.currentUser.ACTIVE_GROUP.CODE !=='consonants'"
                  :class="{
                    'btn-transparent':plan.STATUS.UF_CODE !== 'agreed',
                    'btn-blue': plan.STATUS.UF_CODE === 'agreed'
                  }"
                  :disabled="plan.STATUS.UF_CODE !== 'agreed'" @click="signList(plan.ID)">лист согласования
          </button>
          <button type="button" class="btn btn-reset" @click="closeEditPlan">
            закрыть
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Утверждение Плана закупки - Добавить План закупки  -->

  <!-- Утверждение Плана закупки - Лист согласование плана закупки-->
  <div class="modal" id="modal-plan-zakupku-ypz-signatories" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered" v-if="showStatus">
      <div class="modal-content">
        <div class="modal-header df-fc df-al stl-mb-15">
          <div style="border-bottom: 1px solid #cecece; padding-bottom: 8px;">
            <h5 class="modal-title stl-mb-5">Согласование протокола от {{ plan.UF_DATE_CREATE }}</h5>

            <template v-if="plan.STATUS.UF_CODE === 'revoked'">
              <span class="stl-fs-12 stl-clr-red">Отозвано - {{
                  moment(plan.UF_DATE_MODIFIED).format('DD.MM.YYYY')
                }} ({{ (plan.MODIFIED_BY) ? plan.MODIFIED_BY.FULL_NAME : '' }})</span>
            </template>
            <template v-else>
              <span class="stl-fs-12">Предмет закупки - {{ plan.UF_SUBJECT }}</span>
            </template>
          </div>
        </div>

        <div class="modal-body stl-mb-20">
          <form action="#">
            <div class="modal-grid-row">
              <ol class="list-signatories">
                <li v-for="item in plan.USER_SIGNS.filter(v => v.UF_USER_ID !== 0)" :key="item">
                  <span class="list-signatories__name">{{ item.USER.FULL_NAME }}</span>

                  <template v-if="plan.STATUS.UF_CODE !== 'revoked' && item.STATUS && item.STATUS.UF_NAME">
                    <span class="list-signatories__status stl-clr-green"
                          v-if="item.STATUS && item.STATUS.UF_CODE==='agreed'">
                          {{ item.STATUS.UF_NAME }}
                          {{ moment(item.STATUS.UF_DATE_MODIFIED).format('DD.MM.YYYY') }}
                    </span>
                    <span class="list-signatories__status stl-clr-orange" v-else>{{ item.STATUS.UF_NAME }}</span>
                  </template>
                </li>
              </ol>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-reset"
                  @click="showStatus = false;shared.closeModal('#modal-plan-zakupku-ypz-signatories')">
            закрыть
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal" id="plan-revoke" tabindex="-1">
    <div class="modal-dialog  modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Укажите причину отклонения</h5>
          <button type="button" class="btn-close" @click="shared.closeModal('#plan-revoke')"
                  aria-label="Закрыть"></button>
        </div>
        <div class="modal-body">
          <form action="#">
            <div class="df-fc modal-grid-row">
              <label for="" class="form-label">Причина <span class="c-c-danger">*</span></label>
              <textarea v-model="revoke.comment" class="c-input c-textarea c-textarea-n-resize"
                        placeholder="Ваш текст"></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-reset" @click="showEditPlan(plan.ID)">Закрыть
          </button>
          <button type="button" class="btn btn-red" :disabled="!revoke.comment || disableRevoke" @click="planRevoke()">
            Отклонить
          </button>
        </div>
      </div>
    </div>
  </div>
  <ECP :sign-title="'Подписать протокол'"
       :sign-data="plan.UF_HASH" :close-modal="closeModalSign"
       :sign-callback="sendEds" v-if="showSign"></ECP>
</template>
